import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, ShieldAlert, ShieldX, RefreshCw, PlayCircle, ChevronDown, ChevronUp, ExternalLink, RotateCw, Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLOR_MAP = {
  green:  { bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-300 dark:border-emerald-700', text: 'text-emerald-800 dark:text-emerald-200', badge: 'bg-emerald-500', Icon: ShieldCheck },
  yellow: { bg: 'bg-amber-50 dark:bg-amber-900/20',     border: 'border-amber-300 dark:border-amber-700',     text: 'text-amber-800 dark:text-amber-200',     badge: 'bg-amber-500',   Icon: ShieldAlert },
  red:    { bg: 'bg-rose-50 dark:bg-rose-900/20',       border: 'border-rose-300 dark:border-rose-700',       text: 'text-rose-800 dark:text-rose-200',       badge: 'bg-rose-500',    Icon: ShieldX },
};

const IsolirSanitizerWidget = () => {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [strayList, setStrayList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [retryingRouterId, setRetryingRouterId] = useState(null);
  const [showStray, setShowStray] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statusRes, histRes] = await Promise.all([
        axios.get(`${API_URL}/api/v2/isolir-sanitizer/status`, { headers }),
        axios.get(`${API_URL}/api/v2/isolir-sanitizer/history?days=7`, { headers }),
      ]);
      if (statusRes.data.success) setStatus(statusRes.data);
      if (histRes.data.success) setHistory(histRes.data.data || []);
    } catch (e) {
      console.error('sanitizer status err', e);
    }
  }, [token]);

  const fetchStrayList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/v2/isolir-sanitizer/stray-customers`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) setStrayList(res.data.data || []);
    } catch (e) {
      toast.error('Gagal load list stray');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const triggerRun = async (routerId = null, label = 'Fix Now') => {
    if (routerId) setRetryingRouterId(routerId);
    else setRunning(true);
    try {
      await axios.post(`${API_URL}/api/v2/isolir-sanitizer/run`,
        routerId ? { router_id: routerId } : {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${label} dimulai. Menunggu selesai...`);
      // Poll: refresh 3x with 4s interval to catch new run
      for (let i = 0; i < 3; i++) {
        await new Promise(r => setTimeout(r, 4000));
        await fetchStatus();
      }
      if (showStray) await fetchStrayList();
    } catch (e) {
      toast.error(`Gagal trigger: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRunning(false);
      setRetryingRouterId(null);
    }
  };

  const toggleStray = async () => {
    if (!showStray) await fetchStrayList();
    setShowStray(!showStray);
  };

  if (!status) {
    return (
      <Card className="border-slate-200 dark:border-slate-700" data-testid="isolir-sanitizer-widget-loading">
        <CardContent className="p-4 text-sm text-slate-500 dark:text-slate-400 flex items-center gap-2">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Memuat status sanitizer...
        </CardContent>
      </Card>
    );
  }

  const c = COLOR_MAP[status.color] || COLOR_MAP.green;
  const { Icon } = c;
  const last = status.last_run;
  const unreachable = last?.unreachable_routers || [];

  return (
    <Card
      className={`${c.bg} ${c.border} border-2 transition-all`}
      data-testid="isolir-sanitizer-widget"
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className={`w-11 h-11 rounded-lg ${c.badge} flex items-center justify-center shrink-0`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className={`text-base sm:text-lg font-bold ${c.text}`}>
                Auto-Sanitizer Isolir
                <Badge className={`ml-2 ${c.badge} text-white text-[10px] uppercase`}>
                  {status.color}
                </Badge>
              </CardTitle>
              <p className={`text-xs sm:text-sm ${c.text} opacity-90 mt-0.5`}>
                {status.summary}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={fetchStatus}
              className="h-8 text-xs dark:border-slate-600 dark:text-slate-200"
              data-testid="isolir-sanitizer-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() => triggerRun()}
              disabled={running}
              className="h-8 text-xs bg-slate-800 hover:bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
              data-testid="isolir-sanitizer-fix-now-btn"
            >
              <PlayCircle className={`w-3.5 h-3.5 mr-1 ${running ? 'animate-pulse' : ''}`} />
              {running ? 'Running...' : 'Fix Now'}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Metrics row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <MetricBox label="Stray Sekarang" value={status.stray_count} accent={status.stray_count > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'} testid="metric-stray-now" />
          <MetricBox label="Isolir Online" value={status.isolated_online} accent="text-slate-800 dark:text-slate-100" testid="metric-isolated-online" />
          <MetricBox label="Last Kick" value={last?.kicked ?? '-'} accent="text-slate-800 dark:text-slate-100" testid="metric-last-kicked" />
          <MetricBox label="Last Age" value={last ? `${last.age_minutes}m` : '-'} accent="text-slate-800 dark:text-slate-100" testid="metric-last-age" />
        </div>

        {/* Unreachable routers */}
        {unreachable.length > 0 && (
          <div className="rounded-lg bg-rose-100 dark:bg-rose-950/40 border border-rose-300 dark:border-rose-800 p-3 space-y-2" data-testid="unreachable-routers-panel">
            <div className="text-xs font-semibold text-rose-800 dark:text-rose-200 uppercase tracking-wide">
              Router unreachable saat kick terakhir ({unreachable.length})
            </div>
            <div className="space-y-1.5">
              {unreachable.map(r => (
                <div key={r.id} className="flex items-center justify-between gap-2 text-xs bg-white/50 dark:bg-slate-900/40 rounded px-2 py-1.5">
                  <div className="min-w-0">
                    <div className="font-medium text-rose-900 dark:text-rose-100 truncate">
                      #{r.id} {r.name}
                    </div>
                    <div className="text-rose-700 dark:text-rose-300 opacity-80 truncate">
                      {r.host}:{r.port} — {r.reason || 'no reason'}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => triggerRun(r.id, `Retry ${r.name}`)}
                    disabled={retryingRouterId === r.id}
                    className="h-7 text-[11px] shrink-0 border-rose-400 text-rose-800 dark:border-rose-600 dark:text-rose-200"
                    data-testid={`retry-router-${r.id}-btn`}
                  >
                    <RotateCw className={`w-3 h-3 mr-1 ${retryingRouterId === r.id ? 'animate-spin' : ''}`} />
                    Retry
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Toggles */}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={toggleStray}
            className={`h-7 text-xs ${c.text} hover:bg-white/60 dark:hover:bg-slate-800/60`}
            data-testid="toggle-stray-list-btn"
          >
            {showStray ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
            Detail Stray ({status.stray_count})
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowHistory(!showHistory)}
            className={`h-7 text-xs ${c.text} hover:bg-white/60 dark:hover:bg-slate-800/60`}
            data-testid="toggle-history-btn"
          >
            {showHistory ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
            <Clock className="w-3 h-3 mr-1" />
            History 7 hari ({history.length})
          </Button>
        </div>

        {/* Stray drill-down */}
        {showStray && (
          <div className="rounded-lg bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 p-2 max-h-64 overflow-y-auto" data-testid="stray-list-panel">
            {loading ? (
              <div className="text-xs text-slate-500 p-2 flex items-center gap-2">
                <RefreshCw className="w-3 h-3 animate-spin" /> Memuat...
              </div>
            ) : strayList.length === 0 ? (
              <div className="text-xs text-slate-500 p-2 italic">Tidak ada customer stray. Semua bersih.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-slate-500 dark:text-slate-400 uppercase text-[10px]">
                  <tr>
                    <th className="text-left p-1.5">Akun</th>
                    <th className="text-left p-1.5">Nama</th>
                    <th className="text-left p-1.5">PPPoE</th>
                    <th className="text-left p-1.5">Framed-IP</th>
                    <th className="text-left p-1.5">Router / Cabang</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {strayList.map(s => (
                    <tr key={`${s.customer_id}-${s.pppoe_username}`} data-testid={`stray-row-${s.customer_id}`}>
                      <td className="p-1.5 font-mono text-[11px] text-slate-700 dark:text-slate-300">{s.account_number}</td>
                      <td className="p-1.5 truncate max-w-[140px] text-slate-800 dark:text-slate-200">{s.customer_name}</td>
                      <td className="p-1.5 font-mono text-[11px] text-slate-700 dark:text-slate-300">{s.pppoe_username}</td>
                      <td className="p-1.5 font-mono text-[11px] text-rose-700 dark:text-rose-300">{s.framed_ip}</td>
                      <td className="p-1.5 text-[11px] text-slate-600 dark:text-slate-400">
                        {s.router_name || '-'} <span className="text-slate-400">/</span> {s.branch_name || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* History table */}
        {showHistory && (
          <div className="rounded-lg bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 p-2 max-h-64 overflow-y-auto" data-testid="history-panel">
            {history.length === 0 ? (
              <div className="text-xs text-slate-500 p-2 italic">Belum ada history.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-slate-500 dark:text-slate-400 uppercase text-[10px]">
                  <tr>
                    <th className="text-left p-1.5">Waktu</th>
                    <th className="text-right p-1.5">Scan</th>
                    <th className="text-right p-1.5">Stray</th>
                    <th className="text-right p-1.5">Kick</th>
                    <th className="text-right p-1.5">Err</th>
                    <th className="text-left p-1.5">Trigger</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {history.map(h => (
                    <tr key={h.id} data-testid={`history-row-${h.id}`}>
                      <td className="p-1.5 font-mono text-[11px] text-slate-700 dark:text-slate-300">{h.started_at}</td>
                      <td className="p-1.5 text-right text-slate-700 dark:text-slate-300">{h.scan_total}</td>
                      <td className={`p-1.5 text-right font-semibold ${h.stray_count > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500'}`}>{h.stray_count}</td>
                      <td className="p-1.5 text-right text-emerald-600 dark:text-emerald-400">{h.kicked}</td>
                      <td className={`p-1.5 text-right ${h.errors > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500'}`}>{h.errors}</td>
                      <td className="p-1.5 text-[11px] text-slate-600 dark:text-slate-400 truncate max-w-[160px]">
                        {h.triggered_by}{h.dry_run ? ' (dry)' : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Footer info */}
        <div className={`text-[11px] ${c.text} opacity-70 flex items-center gap-1 flex-wrap`}>
          <span>Subnet isolir: <code className="font-mono">{status.subnets.join(', ')}</code></span>
          <span className="mx-1">•</span>
          <span>Cron daily 03:30 WIB</span>
          {last && (<>
            <span className="mx-1">•</span>
            <span>Last: {last.triggered_by}</span>
          </>)}
        </div>
      </CardContent>
    </Card>
  );
};

const MetricBox = ({ label, value, accent, testid }) => (
  <div
    className="rounded-lg bg-white/70 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 p-2.5"
    data-testid={testid}
  >
    <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">{label}</div>
    <div className={`text-xl sm:text-2xl font-bold mt-0.5 ${accent}`}>{value}</div>
  </div>
);

export default IsolirSanitizerWidget;
